import ApolloClient from 'apollo-client';
import { ERNIE_TYPES } from 'shared/constants';
import createDutchiePosLoyaltyMember from 'shared/graphql/reward/mutations/create-dutchie-pos-loyalty-member.gql';
import createRedemption from 'shared/graphql/referral/mutations/create-redemtion.gql';
import { GqlDispensaries, GqlFindOrCreateReferralCodeMutation } from 'types/graphql';
import { NextRouter } from 'next/router';
import {
  getDefaultRewardsProgramDisplayName,
  getDefaultRewardsProgramDisplayDescription,
} from 'shared/core/helpers/dispensaries';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { Statuses, UseLoyaltyReturn, ShowErnie } from './types';

type Dependencies = {
  isShowReferralsEnabled?: boolean | null;
  dispensary: GqlDispensaries;
  user: {
    id: string;
    phone: string;
    birthday: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  apolloClient: ApolloClient<object>;
  showErnie: ShowErnie;
};

type DutchiePOSLoyaltyProps = {
  status: Statuses;
  setStatus: (status: Statuses) => void;
  deps: Dependencies;
  router: NextRouter;
  referralData?: GqlFindOrCreateReferralCodeMutation['findOrCreateReferralCode'] | null;
};

export const createDutchiePosLoyalty = ({
  status,
  setStatus,
  deps,
  router,
  referralData,
}: DutchiePOSLoyaltyProps): UseLoyaltyReturn => {
  const { dispensary, user: User, apolloClient, showErnie, isShowReferralsEnabled } = deps;

  const isReferralsRoute = isReferralsRouteUtils(router);
  const referralCode = router.isReady ? String(router.query.code) : '';

  const programName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayName ||
    getDefaultRewardsProgramDisplayName({ location: dispensary.location ?? {} });
  const programDescription =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayDescription ||
    getDefaultRewardsProgramDisplayDescription({ location: dispensary.location ?? {} });

  const createUserAndPin = async (): Promise<void> => {
    setStatus(Statuses.loading);
    try {
      await apolloClient.mutate({
        mutation: createDutchiePosLoyaltyMember,
        variables: {
          senderUniqueId: referralData?.senderPosUniqueId ?? '',
          dispensaryId: dispensary.id,
          userId: User.id,
        },
      });

      if (isShowReferralsEnabled && isReferralsRoute && referralCode) {
        await createReferralRedemption();
      }

      setStatus(Statuses.success);
    } catch (error) {
      showErnie('Error connecting account, please try again.', ERNIE_TYPES.DANGER);
      setStatus(Statuses.terms);
    }
  };

  const createReferralRedemption = async (): Promise<void> => {
    setStatus(Statuses.loading);
    try {
      await apolloClient.mutate({
        mutation: createRedemption,
        variables: {
          code: referralCode,
          receiverUserId: User.id,
        },
      });

      showErnie('Success! Complete an order to also receive a Referral Reward.', ERNIE_TYPES.SUCCESS);
      setStatus(Statuses.success);

      const { customDomainSettings, cName } = dispensary;
      const hostUrl = window.location.host;

      if (cName) {
        let redirectUrl = `https://${hostUrl}/stores/${cName}`;

        const customDomain = customDomainSettings?.domain ?? '';

        if (customDomain) {
          redirectUrl = `https://${customDomain}/stores/${cName}`;
        }

        window.location.href = redirectUrl;
      }
    } catch (error) {
      showErnie('Error creating redemption. Please try again.', ERNIE_TYPES.DANGER);
      setStatus(Statuses.terms);
    }
  };

  return {
    status,
    setStatus,
    programName,
    programDescription,
    createUserAndPin,
    processPin: async () => Promise.resolve(),
    resendPin: async () => Promise.resolve(),
  };
};
